import { ItemStatus } from '../../../interfaces/enums'
import { SummaryListProps } from './SummaryList.types'
import {
  SummaryListWrapper,
  SummaryListHeader,
  SummaryHeading,
  SummaryWrapper,
  SummaryItemWrapper,
  SummaryTotalWrapper,
  SummaryHeadingCount
} from './SummaryList.styles'
import Message from '../Message'
import useId from '../../hooks/useId'
import SummaryListTotals from './SummaryListTotals/SummaryListTotals'
import SummaryListItem from './SummaryListItem/SummaryListItem'
import { IProduct } from '../../../interfaces/catalog'
import SummaryListGroup from './SummaryListGroup/SummaryListGroup'
import { getTotalItems } from './SummaryList.utils'
import SummaryHeader from './SummaryHeader/SummaryHeader'
import { getPricePerPackageType } from '../ItemCard/ItemCard.utils'

const SummaryList = (SummaryProps: SummaryListProps) => {
  const {
    cart = {
      total: 0,
      subtotal: 0,
      items: [],
      groups: [],
      activeFees: []
    },
    summary = {
      total: 0,
      subtotal: 0,
      items: [],
      groups: []
    },
    status = ItemStatus.OK,
    unitsLabelProps,
    unitsLabel = '{count} productos',
    subTotalLabel,
    totalLabel,
    packagePriceMap = {},
    headingQuantity = '',
    headingProduct = '',
    headingSubtotal = '',
    heading,
    subtitle,
    labelMap,
    priceProps,
    emptyMessage,
    errorMessage,
    loadingMessage,
    styles,
    className,
    fieldsToDisplay = [
      'quantity',
      'name',
      'description',
      'unitPrice',
      'totalFee',
      'promotion'
    ],
    summaryFieldsToDisplay = [
      'subtotal',
      'discounts',
      'perceptions',
      'taxes',
      'administrativeServices'
    ],
    'data-testid': testid = 'Summary',
    ...otherProps
  } = SummaryProps
  const { getTestId } = useId(testid)
  const hasCustomFee =
    summary?.customFields && summary?.customFields?.summary?.length > 0
  const { items, groups, total, subtotal } = hasCustomFee ? summary : cart
  const itemsEmpty = !items?.length
  const groupsEmpty = !groups?.length
  const isEmpty = itemsEmpty && groupsEmpty
  const headers = [headingQuantity, headingProduct, headingSubtotal]

  return (
    <>
      {!isEmpty && status !== ItemStatus.LOADING && (
        <SummaryListWrapper
          className={`SummaryList ${className || ''}`}
          gap={18}
          sx={styles}
          data-testid={getTestId()}
          {...otherProps}>
          {heading && (
            <SummaryListHeader
              className="SummaryListHeader"
              data-testid={getTestId('header')}>
              <SummaryHeading
                data-testid={getTestId('heading')}
                value={heading}
              />
              {groupsEmpty && !itemsEmpty && (
                <SummaryWrapper>
                  <>
                    <SummaryHeading data-testid={getTestId('subtitle')}>
                      {subtitle}
                    </SummaryHeading>
                    <SummaryHeadingCount
                      {...unitsLabelProps}
                      value={unitsLabel.replace(
                        '{count}',
                        String(getTotalItems(items))
                      )}
                    />
                  </>
                </SummaryWrapper>
              )}
            </SummaryListHeader>
          )}
          <SummaryItemWrapper>
            {groupsEmpty && !itemsEmpty && <SummaryHeader headers={headers} />}
            {items?.map((item: IProduct, index: number) => {
              const itemKey = item.id || item.sku || `item-${index}`
              const packagePrice = getPricePerPackageType(item, packagePriceMap)
              return (
                <SummaryListItem
                  fieldsToDisplay={fieldsToDisplay}
                  key={itemKey}
                  item={{ ...item, price: packagePrice }}
                  priceProps={priceProps}
                  data-testid={getTestId(itemKey)}
                  {...priceProps}
                />
              )
            })}
            {groups?.map((group) => {
              return (
                <SummaryListGroup
                  fieldsToDisplay={fieldsToDisplay}
                  headers={headers}
                  key={group.groupBy.toString()}
                  labelMap={labelMap}
                  priceProps={priceProps}
                  unitsLabel={unitsLabel}
                  unitsLabelProps={unitsLabelProps}
                  group={group}
                />
              )
            })}
          </SummaryItemWrapper>
          <SummaryTotalWrapper>
            <SummaryListTotals
              totalLabel={totalLabel}
              subTotalLabel={subTotalLabel}
              total={total}
              subTotal={subtotal}
              priceProps={priceProps}
              activeFees={cart.activeFees}
              labelMap={labelMap}
              summary={summary}
              hasCustomFee={hasCustomFee}
              summaryFieldsToDisplay={summaryFieldsToDisplay}
              data-testid={getTestId('totals')}
            />
          </SummaryTotalWrapper>
        </SummaryListWrapper>
      )}
      {isEmpty && status === ItemStatus.OK && emptyMessage && (
        <Message data-testid={getTestId('empty-message')} {...emptyMessage} />
      )}
      {isEmpty && status === ItemStatus.ERROR && errorMessage && (
        <Message data-testid={getTestId('error-message')} {...errorMessage} />
      )}
      {status === ItemStatus.LOADING && loadingMessage && (
        <Message
          data-testid={getTestId('loading-message')}
          {...loadingMessage}
        />
      )}
    </>
  )
}

export default SummaryList
